import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import { buildFAQPageSchema } from '@/lib/schema';
import { analytics } from '@/lib/analytics';
import { EVENTS } from '@/lib/analytics/config';

const faqs = [
  {
    question: 'Is my code safe? What access do you get?',
    answer: `We use read-only OAuth access to GitHub. We read pull request metadata, commit history, and review activity. Your code stays yours: we never clone your repository or read your files. The only code we store is the short snippet GitHub attaches to an inline review comment, which is how we measure review quality. We never receive secrets, environment variables, or write permissions, and you can revoke access at any time from your GitHub settings.`,
    defaultOpen: true,
  },
  {
    question: 'What\'s the catch? Why is it free?',
    answer: `Roughly 1 in 5 engineering leaders who see their metrics through CodePulse decide they want ongoing visibility and become paying customers. That's our business model - not selling your data, not ads, just software subscriptions for teams that find value in continuous monitoring. Even if you never pay us a cent, you'll walk away with genuine insights about your engineering organization.`,
  },
  {
    question: 'Will my developers push back on this?',
    answer: `This is the most common concern we hear, and honestly -- most teams welcome it. CodePulse shows team-level patterns, not individual surveillance metrics. There's no keystroke tracking, no screen time, no individual scoring. What engineers actually see is where their PRs get stuck, who's overloaded with reviews, and whether the process is working. Those are frustrations they already have -- CodePulse just makes them visible. Several of our teams were set up by the engineers themselves, not management.`,
  },
  {
    question: 'How long until I see useful data?',
    answer: `Within 5 minutes of connecting, you'll see your Engineering Health Score and basic metrics. We automatically sync 6 months of historical data, so trends and comparisons are available immediately. Data refreshes automatically every 15 minutes after that.`,
  },
  {
    question: 'Can I use this with private repositories?',
    answer: `Yes! CodePulse works with both public and private repositories. The OAuth permissions work the same way - we only access the repositories you explicitly choose to connect, and we maintain the same strict read-only access and encryption standards.`,
  },
  {
    question: 'What if I want to delete my data?',
    answer: `You have full control. You can delete all your organization's data with one click from the settings page. You can also revoke OAuth access directly from GitHub (Settings > Applications > Authorized OAuth Apps). Once deleted, your data is permanently removed from our systems.`,
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(3); // "How long until I see useful data?" open by default (shifted by new FAQ at index 2)
  const [headerRef, isHeaderInView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [faqsRef, isFaqsInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

  const toggleFAQ = (index: number) => {
    const isOpening = openIndex !== index;
    if (isOpening) {
      analytics.track({ name: EVENTS.FAQ_EXPANDED, params: { question: faqs[index].question } });
    }
    setOpenIndex(isOpening ? index : null);
  };

  return (
    <section className="py-12 bg-white dark:bg-gray-900">
      {/* FAQPage schema for rich results / AI search citations */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildFAQPageSchema(faqs)) }}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 ${isHeaderInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Common Questions
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Quick answers before you connect
          </p>
        </div>

        {/* FAQ Accordion */}
        <div ref={faqsRef} className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === index
                  ? 'border-primary-200 dark:border-primary-700 bg-primary-50/30 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              } ${isFaqsInView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: `${index * 75}ms` }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                aria-expanded={openIndex === index}
                aria-controls={`faq-panel-${index}`}
                id={`faq-button-${index}`}
              >
                <span className="font-semibold text-gray-900 dark:text-white pr-4 pointer-events-none">
                  {faq.question}
                </span>
                <span className={`transition-transform duration-300 pointer-events-none ${openIndex === index ? 'rotate-180' : ''}`}>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 pointer-events-none ${
                      openIndex === index ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'
                    }`}
                    aria-hidden="true"
                  />
                </span>
              </button>
              {/* Smooth height transition using CSS grid */}
              <div
                className="grid transition-all duration-300 ease-out"
                style={{
                  gridTemplateRows: openIndex === index ? '1fr' : '0fr',
                }}
                id={`faq-panel-${index}`}
                role="region"
                aria-labelledby={`faq-button-${index}`}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
